import { Link } from "react-router-dom";
import { ArrowUpRight, Bot, LineChart, Salad, Sparkles } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useCurrentPartner } from "@/hooks/useCurrentPartner";
import { DashCard, DashHeading } from "@/components/dashboard";
import { IssueCodeDialog } from "@/components/IssueCodeDialog";
import { formatDuration, formatMoney } from "@/lib/format";

export default function OverviewPage() {
  const { data: partner } = useCurrentPartner();

  if (!partner) return <div className="text-sm text-neutral-500">Cargando…</div>;

  return (
    <div>
      <DashHeading
        title={`Hola, ${partner.name}`}
        description="Emite códigos para tus clientes y consulta tus precios y comisión."
      />

      {/* Precios (con botón Emitir código como acción) + Banner */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <DashCard
          title="Precios vigentes"
          action={
            <IssueCodeDialog
              partner={partner}
              trigger={
                <button
                  type="button"
                  className="group relative inline-flex items-center gap-2 h-9 px-4 rounded-md bg-ink text-white text-[13px] font-medium tracking-tight overflow-hidden transition-all shadow-[0_1px_2px_rgba(0,0,0,0.08)] hover:shadow-[0_10px_24px_-10px_rgba(0,0,0,0.35),_0_0_0_1px_rgba(193,199,206,0.35)] active:translate-y-[1px]"
                >
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 transition-all duration-700 group-hover:opacity-100 group-hover:translate-x-[380%]"
                  />
                  <span className="relative">Emitir código</span>
                  <ArrowUpRight
                    className="relative h-3.5 w-3.5 text-silver-hi transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    strokeWidth={2.25}
                  />
                </button>
              }
            />
          }
        >
          {partner.pricing.length === 0 ? (
            <p className="text-sm text-neutral-500">
              NiuFit aún no ha configurado precios para tu partner. Contáctanos para activarlos.
            </p>
          ) : (
            <div className="divide-y divide-neutral-100 -mt-2">
              {partner.pricing.map((p) => (
                <div key={p.duration_days} className="flex items-center justify-between py-4">
                  <div>
                    <div className="text-base font-semibold text-neutral-900">
                      {formatDuration(p.duration_days)}
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5">{p.duration_days} días</div>
                  </div>
                  <div className="text-lg font-semibold tabular-nums text-neutral-900">
                    {formatMoney(p.price_cents, partner.currency)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DashCard>

        <PartnerBanner partnerName={partner.name} />
      </div>
    </div>
  );
}

/* ─────────────── Banner NiuFit acompaña (editorial cinemático) ─────────────── */

function PartnerBanner({ partnerName }: { partnerName: string }) {
  return (
    <div className="group relative rounded-lg overflow-hidden bg-ink text-white flex flex-col h-[380px] self-start lg:sticky lg:top-6">
      {/* Halo plateado principal */}
      <div
        aria-hidden
        className="absolute -top-32 -right-24 h-96 w-96 rounded-full blur-[110px] opacity-[0.22] pointer-events-none transition-opacity duration-700 group-hover:opacity-[0.32]"
        style={{
          background: "radial-gradient(circle, hsl(var(--silver-hi)) 0%, transparent 65%)",
        }}
      />
      {/* Halo secundario en la parte inferior izquierda */}
      <div
        aria-hidden
        className="absolute -bottom-32 -left-16 h-72 w-72 rounded-full blur-[100px] opacity-[0.12] pointer-events-none"
        style={{
          background: "radial-gradient(circle, hsl(var(--silver)) 0%, transparent 60%)",
        }}
      />
      {/* Trama de líneas verticales muy sutiles */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg, rgba(255,255,255,0.6) 0 1px, transparent 1px 96px)",
        }}
      />

      {/* Contenido principal — flex-1 para empujar el CTA al fondo */}
      <div className="relative z-10 flex-1 p-7 pb-5">
        {/* Eyebrow con dot animado */}
        <div className="inline-flex items-center gap-2 text-[10px] font-mono-num uppercase tracking-[0.22em] text-white/45">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-silver animate-pulse-dot" />
            NiuFit
          </span>
          <span className="text-white/20">/</span>
          <span>Partners</span>
        </div>

        {/* Headline hero — más grande y con más presencia */}
        <h3 className="mt-5 font-display text-[32px] xl:text-[38px] font-medium leading-[1.05] tracking-tight">
          NiuFit acompaña a
          <br />
          <span className="text-silver-metal italic font-medium">{partnerName}</span>.
        </h3>

        {/* Mensaje / value prop */}
        <p className="mt-5 text-[13px] text-white/60 leading-relaxed max-w-md">
          Tus clientes merecen un servicio integral que los acompañe hasta su casa
          y los ayude a lograr sus objetivos.
        </p>

        {/* Divider fino tipo hairline */}
        <div className="mt-6 mb-5 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        {/* 3 features en grid horizontal con íconos */}
        <div className="grid grid-cols-3 gap-2">
          <FeatureBlock icon={Bot} label="Coach IA" sublabel="24/7 en el celular" />
          <FeatureBlock icon={Salad} label="Nutrición" sublabel="Planes personales" />
          <FeatureBlock icon={LineChart} label="Seguimiento" sublabel="Progreso diario" />
        </div>
      </div>

      {/* CTA sutil al fondo con borde superior */}
      <div className="relative z-10 border-t border-white/[0.06] px-7 py-3.5">
        <Link
          to="/tutorials"
          className="group/cta inline-flex items-center gap-1.5 text-[11px] font-mono-num uppercase tracking-[0.16em] text-white/55 hover:text-white transition-colors"
        >
          <Sparkles className="h-3 w-3 text-silver-hi" strokeWidth={2} />
          Conoce cómo presentarlo
          <ArrowUpRight
            className="h-3 w-3 transition-transform duration-300 group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5"
            strokeWidth={2.25}
          />
        </Link>
      </div>
    </div>
  );
}

function FeatureBlock({
  icon: Icon,
  label,
  sublabel,
}: {
  icon: LucideIcon;
  label: string;
  sublabel: string;
}) {
  return (
    <div className="relative rounded-md border border-white/[0.06] bg-white/[0.02] px-3 py-3 transition-colors hover:border-white/[0.12] hover:bg-white/[0.04]">
      <Icon className="h-4 w-4 text-silver-hi mb-2" strokeWidth={1.75} />
      <div className="text-[11px] font-semibold text-white/90 tracking-tight leading-tight">
        {label}
      </div>
      <div className="text-[10px] text-white/40 leading-snug mt-0.5">{sublabel}</div>
    </div>
  );
}
